import { LucideShare2 } from "lucide-react";
import React from "react";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar.tsx";
import { TodoList } from "@/api/types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "wouter";
import { cn } from "@/lib/utils.ts";
import { TaskCount } from "@/views/lists/TaskCount.tsx";
import type { MutableRefObject } from "react";

export interface ListViewProps {
	list: TodoList;
	selected: boolean;
	didDragRef: MutableRefObject<boolean>;
	nested?: boolean;
}

const SidebarListView: React.FC<ListViewProps> = (props) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
		useSortable({
			id: props.list.id,
			disabled: props.list.is_shared,
		});

	const style = {
		transform: CSS.Transform.toString(transform),
		pointerEvents: (isDragging ? "none" : "auto") as any,
	};

	const content = (
		<div className={"flex w-full items-center gap-2 overflow-hidden"}>
			<div
				ref={setActivatorNodeRef}
				{...listeners}
				className={"rounded w-3 h-3 flex-shrink-0 cursor-grab touch-none"}
				style={{ backgroundColor: props.list.color || "#78ded9" }}
			/>
			<span className={"touch-none truncate flex-1"}>{props.list.title}</span>
			{props.list.is_shared && (
				<LucideShare2
					size={14}
					className={"text-muted-foreground flex-shrink-0"}
				/>
			)}
		</div>
	);

	return (
		<>
			<SidebarMenuItem
				key={props.list.id}
				ref={setNodeRef}
				style={style}
				className={cn({ "ml-3": props.nested })}
				{...attributes}
			>
				<SidebarMenuButton
					asChild
					className={cn({ "bg-sidebar-muted": props.selected })}
					size={"lg"}
				>
					<Link
						className={"flex-1 touch-none"}
						to={`/lists/${props.list.id}/items`}
						onClick={(e) => {
							if (props.didDragRef.current) {
								e.preventDefault();
								props.didDragRef.current = false;
							}
						}}
					>
						{content}
						<TaskCount listId={props.list.id} />
					</Link>
				</SidebarMenuButton>
			</SidebarMenuItem>
		</>
	);
};

export { SidebarListView };
